import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Alert,
  Chip,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { createVacationRequest } from '../api/services';
import CloseIcon from '@mui/icons-material/Close';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';

// Design tokens (presentation only)
const ui = {
  primary: '#009db9',
  primaryDark: '#007a8a',
  primarySoft: '#e6f6f9',
  text: '#1f2933',
  muted: '#6b7280',
  border: '#e5e7eb',
  surface: '#f7f9fb',
  danger: '#d32f2f',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#fff',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ui.primary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ui.primary },
  },
  '& label.Mui-focused': { color: ui.primary },
};

const RequestTimeOffModal = ({ open, onClose, onSubmit }) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [dayCount, setDayCount] = useState(0);

  const reasonOptions = [
    'Annual Leave',
    'Personal Reasons',
    'Health and Well-being',
    'Travel and Leisure',
    'Family Obligations',
  ];

  // Calculates days between start and end dates
  useEffect(() => {
    if (startDate && endDate && dayjs(startDate).isValid() && dayjs(endDate).isValid()) {
      const diff = dayjs(endDate).diff(dayjs(startDate), 'day') + 1;
      setDayCount(diff > 0 ? diff : 0);
    } else {
      setDayCount(0);
    }
  }, [startDate, endDate]);

  // Validates form inputs
  const validateForm = () => {
    const newErrors = {};
    const today = dayjs().startOf('day');

    if (!startDate) newErrors.startDate = 'Start date is required.';
    if (!endDate) newErrors.endDate = 'End date is required.';
    if (!reason) newErrors.reason = 'Reason is required.';

    if (startDate && !dayjs(startDate).isValid()) {
      newErrors.startDate = 'Invalid start date.';
    }
    if (endDate && !dayjs(endDate).isValid()) {
      newErrors.endDate = 'Invalid end date.';
    }

    if (startDate && dayjs(startDate).isBefore(today, 'day')) {
      newErrors.startDate = 'Start date must be in the future.';
    }

    if (
      startDate &&
      endDate &&
      dayjs(endDate).isValid() &&
      dayjs(startDate).isValid() &&
      dayjs(endDate).isBefore(dayjs(startDate), 'day')
    ) {
      newErrors.endDate = 'End date must be after start date.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submits vacation request
  const handleSubmit = async () => {
    const isValid = validateForm();
    if (!isValid) return;

    const vacationData = {
      startDate: dayjs(startDate).format('YYYY-MM-DD'),
      endDate: dayjs(endDate).format('YYYY-MM-DD'),
      reason,
    };

    setLoading(true);
    setErrors({});

    try {
      await createVacationRequest(vacationData);
      if (onSubmit) {
        onSubmit(vacationData);
      }
      handleClose();
    } catch (err) {
      if (err.response && err.response.status === 409) {
        setErrors({
          submit: 'You’ve already requested time off for these dates. Please choose different dates or check your existing requests.',
        });
      } else {
        setErrors({ submit: err.message || 'Failed to submit the request. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Closes modal and resets state
  const handleClose = () => {
    setStartDate(null);
    setEndDate(null);
    setReason('');
    setErrors({});
    setDayCount(0);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '20px', m: { xs: 2, sm: 4 }, overflow: 'hidden' } }}
    >
      {/* Header */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: { xs: 2.5, sm: 3 },
          py: 2.5,
          color: '#fff',
          background: `linear-gradient(135deg, ${ui.primaryDark} 0%, ${ui.primary} 60%, #2bbbd4 100%)`,
          '&::before': {
            content: '""',
            position: 'absolute',
            width: 180,
            height: 180,
            borderRadius: '50%',
            top: -90,
            right: 40,
            background: 'rgba(255,255,255,0.10)',
          },
        }}
      >
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FlightTakeoffIcon />
          </Box>
          <Typography component="h2" sx={{ fontWeight: 800, fontSize: '1.25rem' }}>
            Request Time Off
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label="close"
          sx={{
            position: 'relative',
            color: '#fff',
            backgroundColor: 'rgba(255,255,255,0.18)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, pt: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            {errors.submit && (
              <Alert severity="error" variant="filled" sx={{ borderRadius: '12px' }}>
                {errors.submit}
              </Alert>
            )}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 2.5,
              }}
            >
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format="YYYY-MM-DD"
                minDate={dayjs().add(1, 'day')}
                slotProps={{
                  textField: {
                    variant: 'outlined',
                    fullWidth: true,
                    error: !!errors.startDate,
                    helperText: errors.startDate,
                    sx: fieldSx,
                  },
                }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format="YYYY-MM-DD"
                minDate={startDate ? dayjs(startDate).add(1, 'day') : undefined}
                slotProps={{
                  textField: {
                    variant: 'outlined',
                    fullWidth: true,
                    error: !!errors.endDate,
                    helperText: errors.endDate,
                    sx: fieldSx,
                  },
                }}
              />
            </Box>

            {/* Number of days summary */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                borderRadius: '14px',
                border: `1px solid ${dayCount > 0 ? ui.primary : ui.border}`,
                backgroundColor: dayCount > 0 ? ui.primarySoft : ui.surface,
                transition: 'all 0.2s ease',
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  width: 44,
                  height: 44,
                  borderRadius: '12px',
                  backgroundColor: dayCount > 0 ? ui.primary : '#e3e8ec',
                  color: dayCount > 0 ? '#fff' : ui.muted,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <DateRangeOutlinedIcon />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: ui.muted }}>
                  Number of Days
                </Typography>
                <Typography sx={{ fontWeight: 800, color: dayCount > 0 ? ui.primaryDark : ui.muted, fontSize: '1.2rem' }}>
                  {dayCount > 0 ? `${dayCount} day${dayCount !== 1 ? 's' : ''}` : 'Select dates'}
                </Typography>
              </Box>
            </Box>

            {/* Reason */}
            <Box>
              <Typography sx={{ fontWeight: 700, color: ui.text, mb: 1.25 }}>Reason</Typography>
              <Box
                role="radiogroup"
                aria-label="Reason"
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
              >
                {reasonOptions.map((option) => {
                  const selected = reason === option;
                  return (
                    <Chip
                      key={option}
                      label={option}
                      role="radio"
                      aria-checked={selected}
                      clickable
                      onClick={() => setReason(option)}
                      sx={{
                        height: 38,
                        px: 0.5,
                        fontWeight: 600,
                        borderRadius: '10px',
                        border: `1px solid ${selected ? ui.primary : errors.reason ? ui.danger : ui.border}`,
                        backgroundColor: selected ? ui.primary : '#fff',
                        color: selected ? '#fff' : ui.text,
                        '&:hover': { backgroundColor: selected ? ui.primaryDark : ui.primarySoft },
                      }}
                    />
                  );
                })}
              </Box>
              {errors.reason && (
                <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
                  {errors.reason}
                </Typography>
              )}
            </Box>
          </Box>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5, gap: 1, borderTop: `1px solid ${ui.border}` }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ color: ui.muted, textTransform: 'none', fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
          sx={{
            backgroundColor: ui.primary,
            color: '#fff',
            fontWeight: 700,
            py: 1.1,
            px: 4,
            borderRadius: '10px',
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': { backgroundColor: ui.primaryDark, boxShadow: 'none' },
            '&.Mui-disabled': { backgroundColor: '#d5dbe0', color: '#8a949e' },
          }}
        >
          {loading ? 'Submitting...' : 'Submit Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestTimeOffModal;